/**
 * Comprehensive Style Props System
 * Transforms Chakra-style props to CSS properties
 * Includes ALL Chakra UI style props for full API parity
 */

import { breakpoints as defaultBreakpoints } from '../theme/tokens/breakpoints';
// Space scale (Chakra default)
const spaceScale = {
  px: '1px',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  3.5: '0.875rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  28: '7rem',
  32: '8rem',
  36: '9rem',
  40: '10rem',
  44: '11rem',
  48: '12rem',
  52: '13rem',
  56: '14rem',
  60: '15rem',
  64: '16rem',
  72: '18rem',
  80: '20rem',
  96: '24rem',
};

// Size scale (includes space + additional sizes)
const sizeScale = {
  ...spaceScale,
  max: 'max-content',
  min: 'min-content',
  full: '100%',
  '3xs': '14rem',
  '2xs': '16rem',
  xs: '20rem',
  sm: '24rem',
  md: '28rem',
  lg: '32rem',
  xl: '36rem',
  '2xl': '42rem',
  '3xl': '48rem',
  '4xl': '56rem',
  '5xl': '64rem',
  '6xl': '72rem',
  '7xl': '80rem',
  '8xl': '90rem',
  container: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },
};

// Border radius scale
const radiiScale = {
  none: '0',
  sm: '0.125rem',
  base: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  '3xl': '1.5rem',
  full: '9999px',
};

// Font size scale
const fontSizeScale = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
  '6xl': '3.75rem',
  '7xl': '4.5rem',
  '8xl': '6rem',
  '9xl': '8rem',
};

// Font weight scale
const fontWeightScale = {
  hairline: 100,
  thin: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
};

// Line height scale
const lineHeightScale = {
  normal: 'normal',
  none: 1,
  shorter: 1.25,
  short: 1.375,
  base: 1.5,
  tall: 1.625,
  taller: 2,
  3: '.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
};

// Letter spacing scale
const letterSpacingScale = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
};

// Z-index scale
const zIndexScale = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
};

// Shadow scale
const shadowScale = {
  xs: '0 0 0 1px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  outline: '0 0 0 3px rgba(66, 153, 225, 0.6)',
  inner: 'inset 0 2px 4px 0 rgba(0,0,0,0.06)',
  none: 'none',
  'dark-lg': 'rgba(0, 0, 0, 0.1) 0px 0px 0px 1px, rgba(0, 0, 0, 0.2) 0px 5px 10px, rgba(0, 0, 0, 0.4) 0px 15px 40px',
};

// Blur scale
const blurScale = {
  none: '0',
  sm: '4px',
  base: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '40px',
  '3xl': '64px',
};

// Transition scale
const transitionScale = {
  property: {
    common: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
    colors: 'background-color, border-color, color, fill, stroke',
    dimensions: 'width, height',
    position: 'left, right, top, bottom',
    background: 'background-color, background-image, background-position',
  },
  easing: {
    'ease-in': 'cubic-bezier(0.4, 0, 1, 1)',
    'ease-out': 'cubic-bezier(0, 0, 0.2, 1)',
    'ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  duration: {
    'ultra-fast': '50ms',
    faster: '100ms',
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '400ms',
    'ultra-slow': '500ms',
  },
};

/**
 * Resolve a token value from a scale
 */
const resolveToken = (value, scale) => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number' && scale === spaceScale) {
    return scale[value] || `${value * 0.25}rem`;
  }
  if (scale && scale[value] !== undefined) {
    return scale[value];
  }
  return value;
};

/**
 * Mapping of Chakra prop names to CSS property names and scales
 * Format: [cssProp, scale, transform?]
 */
const propConfig = {
  // ==================== MARGIN ====================
  m: ['margin', spaceScale],
  margin: ['margin', spaceScale],
  mt: ['marginTop', spaceScale],
  marginTop: ['marginTop', spaceScale],
  mr: ['marginRight', spaceScale],
  marginRight: ['marginRight', spaceScale],
  me: ['marginInlineEnd', spaceScale],
  marginEnd: ['marginInlineEnd', spaceScale],
  mb: ['marginBottom', spaceScale],
  marginBottom: ['marginBottom', spaceScale],
  ml: ['marginLeft', spaceScale],
  marginLeft: ['marginLeft', spaceScale],
  ms: ['marginInlineStart', spaceScale],
  marginStart: ['marginInlineStart', spaceScale],
  mx: [['marginLeft', 'marginRight'], spaceScale],
  marginX: [['marginLeft', 'marginRight'], spaceScale],
  my: [['marginTop', 'marginBottom'], spaceScale],
  marginY: [['marginTop', 'marginBottom'], spaceScale],

  // ==================== PADDING ====================
  p: ['padding', spaceScale],
  padding: ['padding', spaceScale],
  pt: ['paddingTop', spaceScale],
  paddingTop: ['paddingTop', spaceScale],
  pr: ['paddingRight', spaceScale],
  paddingRight: ['paddingRight', spaceScale],
  pe: ['paddingInlineEnd', spaceScale],
  paddingEnd: ['paddingInlineEnd', spaceScale],
  pb: ['paddingBottom', spaceScale],
  paddingBottom: ['paddingBottom', spaceScale],
  pl: ['paddingLeft', spaceScale],
  paddingLeft: ['paddingLeft', spaceScale],
  ps: ['paddingInlineStart', spaceScale],
  paddingStart: ['paddingInlineStart', spaceScale],
  px: [['paddingLeft', 'paddingRight'], spaceScale],
  paddingX: [['paddingLeft', 'paddingRight'], spaceScale],
  py: [['paddingTop', 'paddingBottom'], spaceScale],
  paddingY: [['paddingTop', 'paddingBottom'], spaceScale],

  // ==================== SPACE (gap) ====================
  gap: ['gap', spaceScale],
  rowGap: ['rowGap', spaceScale],
  columnGap: ['columnGap', spaceScale],
  gridGap: ['gap', spaceScale],
  gridRowGap: ['rowGap', spaceScale],
  gridColumnGap: ['columnGap', spaceScale],

  // ==================== COLOR ====================
  color: ['color', null],
  textColor: ['color', null],
  bg: ['background', null],
  bgColor: ['backgroundColor', null],
  background: ['background', null],
  backgroundColor: ['backgroundColor', null],
  bgGradient: ['backgroundImage', null],
  bgClip: ['backgroundClip', null],
  backgroundClip: ['backgroundClip', null],
  opacity: ['opacity', null],

  // ==================== TYPOGRAPHY ====================
  fontFamily: ['fontFamily', null],
  fontSize: ['fontSize', fontSizeScale],
  fontWeight: ['fontWeight', fontWeightScale],
  lineHeight: ['lineHeight', lineHeightScale],
  letterSpacing: ['letterSpacing', letterSpacingScale],
  textAlign: ['textAlign', null],
  fontStyle: ['fontStyle', null],
  textTransform: ['textTransform', null],
  textDecoration: ['textDecoration', null],
  textDecor: ['textDecoration', null],
  textShadow: ['textShadow', null],
  whiteSpace: ['whiteSpace', null],
  wordBreak: ['wordBreak', null],
  overflowWrap: ['overflowWrap', null],
  textOverflow: ['textOverflow', null],
  noOfLines: [null, null, (value) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: value,
  })],
  isTruncated: [null, null, (value) => value ? ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }) : {}],

  // ==================== LAYOUT ====================
  w: ['width', sizeScale],
  width: ['width', sizeScale],
  h: ['height', sizeScale],
  height: ['height', sizeScale],
  minW: ['minWidth', sizeScale],
  minWidth: ['minWidth', sizeScale],
  maxW: ['maxWidth', sizeScale],
  maxWidth: ['maxWidth', sizeScale],
  minH: ['minHeight', sizeScale],
  minHeight: ['minHeight', sizeScale],
  maxH: ['maxHeight', sizeScale],
  maxHeight: ['maxHeight', sizeScale],
  boxSize: [['width', 'height'], sizeScale],
  d: ['display', null],
  display: ['display', null],
  verticalAlign: ['verticalAlign', null],
  overflow: ['overflow', null],
  overflowX: ['overflowX', null],
  overflowY: ['overflowY', null],
  boxSizing: ['boxSizing', null],

  // ==================== FLEXBOX ====================
  alignItems: ['alignItems', null],
  align: ['alignItems', null],
  alignContent: ['alignContent', null],
  justifyItems: ['justifyItems', null],
  justifyContent: ['justifyContent', null],
  justify: ['justifyContent', null],
  flexWrap: ['flexWrap', null],
  wrap: ['flexWrap', null],
  flexDirection: ['flexDirection', null],
  flexDir: ['flexDirection', null],
  direction: ['flexDirection', null],
  flex: ['flex', null],
  flexGrow: ['flexGrow', null],
  flexShrink: ['flexShrink', null],
  flexBasis: ['flexBasis', sizeScale],
  justifySelf: ['justifySelf', null],
  alignSelf: ['alignSelf', null],
  order: ['order', null],
  placeItems: ['placeItems', null],
  placeContent: ['placeContent', null],
  placeSelf: ['placeSelf', null],

  // ==================== GRID ====================
  gridTemplateColumns: ['gridTemplateColumns', null],
  gridTemplateRows: ['gridTemplateRows', null],
  gridTemplateAreas: ['gridTemplateAreas', null],
  gridTemplate: ['gridTemplate', null],
  gridAutoColumns: ['gridAutoColumns', null],
  gridAutoRows: ['gridAutoRows', null],
  gridAutoFlow: ['gridAutoFlow', null],
  gridColumn: ['gridColumn', null],
  gridRow: ['gridRow', null],
  gridArea: ['gridArea', null],
  gridColumnStart: ['gridColumnStart', null],
  gridColumnEnd: ['gridColumnEnd', null],
  gridRowStart: ['gridRowStart', null],
  gridRowEnd: ['gridRowEnd', null],

  // ==================== BACKGROUND ====================
  bgImage: ['backgroundImage', null],
  backgroundImage: ['backgroundImage', null],
  bgSize: ['backgroundSize', null],
  backgroundSize: ['backgroundSize', null],
  bgPosition: ['backgroundPosition', null],
  backgroundPosition: ['backgroundPosition', null],
  bgRepeat: ['backgroundRepeat', null],
  backgroundRepeat: ['backgroundRepeat', null],
  bgAttachment: ['backgroundAttachment', null],
  backgroundAttachment: ['backgroundAttachment', null],

  // ==================== BORDERS ====================
  border: ['border', null],
  borderWidth: ['borderWidth', null],
  borderStyle: ['borderStyle', null],
  borderColor: ['borderColor', null],
  borderTop: ['borderTop', null],
  borderTopWidth: ['borderTopWidth', null],
  borderTopStyle: ['borderTopStyle', null],
  borderTopColor: ['borderTopColor', null],
  borderRight: ['borderRight', null],
  borderRightWidth: ['borderRightWidth', null],
  borderRightStyle: ['borderRightStyle', null],
  borderRightColor: ['borderRightColor', null],
  borderBottom: ['borderBottom', null],
  borderBottomWidth: ['borderBottomWidth', null],
  borderBottomStyle: ['borderBottomStyle', null],
  borderBottomColor: ['borderBottomColor', null],
  borderLeft: ['borderLeft', null],
  borderLeftWidth: ['borderLeftWidth', null],
  borderLeftStyle: ['borderLeftStyle', null],
  borderLeftColor: ['borderLeftColor', null],
  borderX: [['borderLeft', 'borderRight'], null],
  borderY: [['borderTop', 'borderBottom'], null],
  borderRadius: ['borderRadius', radiiScale],
  rounded: ['borderRadius', radiiScale],
  borderTopLeftRadius: ['borderTopLeftRadius', radiiScale],
  roundedTopLeft: ['borderTopLeftRadius', radiiScale],
  borderTopRightRadius: ['borderTopRightRadius', radiiScale],
  roundedTopRight: ['borderTopRightRadius', radiiScale],
  borderBottomLeftRadius: ['borderBottomLeftRadius', radiiScale],
  roundedBottomLeft: ['borderBottomLeftRadius', radiiScale],
  borderBottomRightRadius: ['borderBottomRightRadius', radiiScale],
  roundedBottomRight: ['borderBottomRightRadius', radiiScale],
  borderTopRadius: [['borderTopLeftRadius', 'borderTopRightRadius'], radiiScale],
  roundedTop: [['borderTopLeftRadius', 'borderTopRightRadius'], radiiScale],
  borderRightRadius: [['borderTopRightRadius', 'borderBottomRightRadius'], radiiScale],
  roundedRight: [['borderTopRightRadius', 'borderBottomRightRadius'], radiiScale],
  borderBottomRadius: [['borderBottomLeftRadius', 'borderBottomRightRadius'], radiiScale],
  roundedBottom: [['borderBottomLeftRadius', 'borderBottomRightRadius'], radiiScale],
  borderLeftRadius: [['borderTopLeftRadius', 'borderBottomLeftRadius'], radiiScale],
  roundedLeft: [['borderTopLeftRadius', 'borderBottomLeftRadius'], radiiScale],

  // ==================== POSITION ====================
  pos: ['position', null],
  position: ['position', null],
  zIndex: ['zIndex', zIndexScale],
  top: ['top', spaceScale],
  right: ['right', spaceScale],
  bottom: ['bottom', spaceScale],
  left: ['left', spaceScale],
  inset: ['inset', spaceScale],
  insetX: [['left', 'right'], spaceScale],
  insetY: [['top', 'bottom'], spaceScale],

  // ==================== SHADOW ====================
  shadow: ['boxShadow', shadowScale],
  boxShadow: ['boxShadow', shadowScale],

  // ==================== FILTER ====================
  filter: ['filter', null],
  blur: ['filter', blurScale, (v) => `blur(${resolveToken(v, blurScale)})`],
  brightness: ['filter', null, (v) => `brightness(${v})`],
  contrast: ['filter', null, (v) => `contrast(${v})`],
  hueRotate: ['filter', null, (v) => `hue-rotate(${v})`],
  invert: ['filter', null, (v) => `invert(${v})`],
  saturate: ['filter', null, (v) => `saturate(${v})`],
  dropShadow: ['filter', null, (v) => `drop-shadow(${v})`],
  backdropFilter: ['backdropFilter', null],
  backdropBlur: ['backdropFilter', blurScale, (v) => `blur(${resolveToken(v, blurScale)})`],

  // ==================== PSEUDO ====================
  _hover: ['&:hover', null],
  _active: ['&:active', null],
  _focus: ['&:focus', null],
  _focusWithin: ['&:focus-within', null],
  _focusVisible: ['&:focus-visible', null],
  _disabled: ['&:disabled, &[data-disabled]', null],
  _readOnly: ['&[readonly], &[data-readonly]', null],
  _before: ['&::before', null],
  _after: ['&::after', null],
  _empty: ['&:empty', null],
  _expanded: ['&[aria-expanded=true], &[data-expanded]', null],
  _checked: ['&[data-checked], &[aria-checked=true]', null],
  _grabbed: ['&[aria-grabbed=true]', null],
  _pressed: ['&[aria-pressed=true]', null],
  _invalid: ['&[aria-invalid=true], &[data-invalid]', null],
  _valid: ['&[data-valid], &[aria-valid=true]', null],
  _loading: ['&[data-loading], &[aria-busy=true]', null],
  _selected: ['&[aria-selected=true], &[data-selected]', null],
  _hidden: ['&[hidden]', null],
  _even: ['&:nth-of-type(even)', null],
  _odd: ['&:nth-of-type(odd)', null],
  _first: ['&:first-of-type', null],
  _last: ['&:last-of-type', null],
  _notFirst: ['&:not(:first-of-type)', null],
  _notLast: ['&:not(:last-of-type)', null],
  _visited: ['&:visited', null],
  _activeLink: ['&[aria-current=page]', null],
  _indeterminate: ['&:indeterminate, &[aria-checked=mixed]', null],
  _placeholder: ['&::placeholder', null],
  _placeholderShown: ['&:placeholder-shown', null],
  _fullScreen: ['&:fullscreen', null],
  _selection: ['&::selection', null],

  // ==================== TRANSFORM ====================
  transform: ['transform', null],
  transformOrigin: ['transformOrigin', null],
  translateX: ['--chakra-translate-x', null],
  translateY: ['--chakra-translate-y', null],
  rotate: ['--chakra-rotate', null],
  skewX: ['--chakra-skew-x', null],
  skewY: ['--chakra-skew-y', null],
  scaleX: ['--chakra-scale-x', null],
  scaleY: ['--chakra-scale-y', null],
  scale: [['--chakra-scale-x', '--chakra-scale-y'], null],

  // ==================== TRANSITION ====================
  transition: ['transition', null],
  transitionProperty: ['transitionProperty', transitionScale.property],
  transitionTimingFunction: ['transitionTimingFunction', transitionScale.easing],
  transitionDuration: ['transitionDuration', transitionScale.duration],
  transitionDelay: ['transitionDelay', transitionScale.duration],
  animation: ['animation', null],

  // ==================== LIST ====================
  listStyleType: ['listStyleType', null],
  listStylePosition: ['listStylePosition', null],
  listStyleImage: ['listStyleImage', null],

  // ==================== OUTLINE ====================
  outline: ['outline', null],
  outlineOffset: ['outlineOffset', null],
  outlineColor: ['outlineColor', null],

  // ==================== CURSOR ====================
  cursor: ['cursor', null],
  userSelect: ['userSelect', null],
  pointerEvents: ['pointerEvents', null],
  resize: ['resize', null],
  objectFit: ['objectFit', null],
  objectPosition: ['objectPosition', null],
  visibility: ['visibility', null],
  isolation: ['isolation', null],
  appearance: ['appearance', null],

  // ==================== SVG ====================
  fill: ['fill', null],
  stroke: ['stroke', null],
  strokeWidth: ['strokeWidth', null],

  // ==================== TABLE ====================
  tableLayout: ['tableLayout', null],
  borderCollapse: ['borderCollapse', null],
  borderSpacing: ['borderSpacing', spaceScale],

  // ==================== CSS VARIABLE ====================
  sx: [null, null, (value) => value], // Pass through sx prop
  __css: [null, null, (value) => value], // Internal styles

  // ==================== ASPECT RATIO ====================
  aspectRatio: ['aspectRatio', null],

  // ==================== SCROLL ====================
  scrollBehavior: ['scrollBehavior', null],
  scrollSnapAlign: ['scrollSnapAlign', null],
  scrollSnapStop: ['scrollSnapStop', null],
  scrollSnapType: ['scrollSnapType', null],
  scrollMargin: ['scrollMargin', spaceScale],
  scrollMarginTop: ['scrollMarginTop', spaceScale],
  scrollMarginBottom: ['scrollMarginBottom', spaceScale],
  scrollMarginLeft: ['scrollMarginLeft', spaceScale],
  scrollMarginRight: ['scrollMarginRight', spaceScale],
  scrollPadding: ['scrollPadding', spaceScale],
  scrollPaddingTop: ['scrollPaddingTop', spaceScale],
  scrollPaddingBottom: ['scrollPaddingBottom', spaceScale],
  scrollPaddingLeft: ['scrollPaddingLeft', spaceScale],
  scrollPaddingRight: ['scrollPaddingRight', spaceScale],

  // ==================== CONTAINER ====================
  containerType: ['containerType', null],
  containerName: ['containerName', null],

  // ==================== PRINT ====================
  printColorAdjust: ['printColorAdjust', null],

  // ==================== INTERACTIVITY ====================
  touchAction: ['touchAction', null],
  willChange: ['willChange', null],
  contentVisibility: ['contentVisibility', null],
  contain: ['contain', null],
};

// List of all style prop keys (for filtering from DOM props)
export const stylePropsKeys = Object.keys(propConfig);

// Pseudo props that need special handling
const pseudoProps = [
  '_hover', '_active', '_focus', '_focusWithin', '_focusVisible',
  '_disabled', '_readOnly', '_before', '_after', '_empty',
  '_expanded', '_checked', '_grabbed', '_pressed', '_invalid',
  '_valid', '_loading', '_selected', '_hidden', '_even', '_odd',
  '_first', '_last', '_notFirst', '_notLast', '_visited',
  '_activeLink', '_indeterminate', '_placeholder', '_placeholderShown',
  '_fullScreen', '_selection'
];

/**
 * Transform Chakra-style props to CSS object
 * @param {object} props - Component props
 * @param {object} theme - Theme object with colors, etc.
 * @returns {object} - CSS object for emotion
 */


// Color-related CSS properties that need token resolution
const colorProps = new Set([
  'color', 'background', 'backgroundColor', 'borderColor',
  'borderTopColor', 'borderRightColor', 'borderBottomColor', 'borderLeftColor',
  'outlineColor', 'fill', 'stroke', 'boxShadow', 'textShadow',
  // Border shorthand often contains color
  'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft'
]);

// Import resolveColor inline to avoid circular deps
const resolveColorToken = (value, themeColors = {}) => {
  if (!value || typeof value !== 'string') return value;

  // If it's already a CSS value, return as-is
  if (value.startsWith('#') ||
    value.startsWith('rgb') ||
    value.startsWith('hsl') ||
    value === 'transparent' ||
    value === 'currentColor' ||
    value === 'inherit' ||
    value === 'none') {
    return value;
  }

  // Try to resolve from theme first (handles 'white', 'black', 'brand.500', 'gray.100' etc.)
  const parts = value.split('.');
  let resolved = themeColors;

  for (const part of parts) {
    if (resolved && typeof resolved === 'object' && part in resolved) {
      resolved = resolved[part];
    } else {
      // If not found in theme, return original value
      // This allows CSS color keywords like 'red', 'blue' to pass through
      return value;
    }
  }

  return typeof resolved === 'string' ? resolved : value;
};

export const transformStyleProps = (props, theme = {}) => {
  const css = {};
  const pseudoStyles = {};
  const themeColors = theme.colors || {};
  const breakpoints = theme.breakpoints || defaultBreakpoints;

  // Helper to get media query
  const getMediaQuery = (bp) => {
    if (bp === 'base') return null;
    const width = breakpoints[bp];
    if (!width) return null;
    return `@media (min-width: ${width})`;
  };

  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null) continue;

    const config = propConfig[key];
    if (!config) continue;

    const [cssProp, scale, transform] = config;

    // Handle pseudo props
    if (pseudoProps.includes(key) && typeof value === 'object') {
      const selector = cssProp;
      pseudoStyles[selector] = transformStyleProps(value, theme);
      continue;
    }

    // Handle responsive object syntax: { base: '...', md: '...' }
    if (typeof value === 'object' && !Array.isArray(value)) {
      Object.entries(value).forEach(([bp, bpValue]) => {
        const query = getMediaQuery(bp);
        const styles = transformStyleProps({ [key]: bpValue }, theme);
        
        if (!query) {
          Object.assign(css, styles);
        } else {
          css[query] = css[query] || {};
          Object.assign(css[query], styles);
        }
      });
      continue;
    }

    // Handle responsive array syntax: ['10px', '20px']
    // Note: We check if the prop itself expects an array (like mx -> [marginLeft, marginRight])
    // But here we are checking if VALUE is array.
    // If cssProp is array (like mx), value can be array of values for breakpoints.
    if (Array.isArray(value)) {
      const orderedBreakpoints = ['base', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'];
      
      value.forEach((bpValue, index) => {
        if (index >= orderedBreakpoints.length) return;
        const bp = orderedBreakpoints[index];
        const query = getMediaQuery(bp);
        const styles = transformStyleProps({ [key]: bpValue }, theme);
        
        if (!query) {
          Object.assign(css, styles);
        } else {
          css[query] = css[query] || {};
          Object.assign(css[query], styles);
        }
      });
      continue;
    }

    // Handle custom transform functions
    if (transform && typeof transform === 'function') {
      const result = transform(value);
      if (typeof result === 'object') {
        Object.assign(css, result);
      } else if (cssProp) {
        css[cssProp] = result;
      }
      continue;
    }

    // Resolve token value
    let resolvedValue = resolveToken(value, scale);

    // For color-related properties, resolve color tokens from theme
    const cssProps = Array.isArray(cssProp) ? cssProp : [cssProp];
    cssProps.forEach((prop) => {
      if (prop && colorProps.has(prop)) {
        resolvedValue = resolveColorToken(resolvedValue, themeColors);
      }
    });

    // Handle array of CSS properties (e.g., mx -> marginLeft + marginRight)
    if (Array.isArray(cssProp)) {
      cssProp.forEach((prop) => {
        css[prop] = colorProps.has(prop) ? resolveColorToken(resolvedValue, themeColors) : resolvedValue;
      });
    } else if (cssProp) {
      css[cssProp] = resolvedValue;
    }
  }

  // Merge pseudo styles
  Object.assign(css, pseudoStyles);

  return css;
};

/**
 * Split props into style props and regular props
 * @param {object} props - All props
 * @returns {[object, object]} - [styleProps, otherProps]
 */
export const splitProps = (props) => {
  const styleProps = {};
  const otherProps = {};

  for (const [key, value] of Object.entries(props)) {
    if (stylePropsKeys.includes(key)) {
      styleProps[key] = value;
    } else {
      otherProps[key] = value;
    }
  }

  return [styleProps, otherProps];
};

export {
  spaceScale,
  sizeScale,
  radiiScale,
  fontSizeScale,
  fontWeightScale,
  lineHeightScale,
  letterSpacingScale,
  zIndexScale,
  shadowScale,
  blurScale,
  transitionScale,
  propConfig,
  resolveToken,
};
